// src/pages/Appointments/Admin/AppointmentAnalytics.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiUsers,
  FiDollarSign,
  FiActivity,
  FiArrowLeft,
} from "react-icons/fi";
import {
  getAppointmentAnalyticsThunk,
  getBranchMetricsThunk,
} from "../../../store/thunks/appointmentThunks";
import { useNavigate } from "react-router-dom";

const AppointmentAnalytics = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { analytics, branchMetrics, loading } = useSelector(
    (state) => state.appointment
  );
  const { selectedBranch, branches } = useSelector((state) => state.branch);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, selectedBranch]);

  const loadAnalytics = () => {
    dispatch(getAppointmentAnalyticsThunk(dateRange));
    if (selectedBranch) {
      dispatch(
        getBranchMetricsThunk({
          branchCode: selectedBranch,
          dateRange,
        })
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/appointments")}
          className="flex items-center text-neutral-dark hover:text-primary mb-4 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          Back to Appointments Dashboard
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-text mb-2">
          Appointment Analytics
        </h1>
        <p className="text-neutral-dark">Performance metrics and insights</p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      {analytics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <MetricCard
              title="Total Appointments"
              value={analytics.totalAppointments || 0}
              trend={analytics.totalAppointmentsTrend}
              icon={FiCalendar}
              color="primary"
            />
            <MetricCard
              title="Conversion Rate"
              value={`${analytics.conversionRate || 0}%`}
              trend={analytics.conversionRateTrend}
              icon={FiDollarSign}
              color="green"
            />
            <MetricCard
              title="Cancellation Rate"
              value={`${analytics.cancellationRate || 0}%`}
              trend={analytics.cancellationRateTrend}
              icon={FiTrendingDown}
              color="red"
              invertTrend
            />
            <MetricCard
              title="No-Show Rate"
              value={`${analytics.noShowRate || 0}%`}
              trend={analytics.noShowRateTrend}
              icon={FiUsers}
              color="orange"
              invertTrend
            />
          </div>

          {/* Status Breakdown */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-text mb-4">
              Status Breakdown
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.statusBreakdown &&
                Object.entries(analytics.statusBreakdown).map(
                  ([status, count]) => (
                    <div
                      key={status}
                      className="p-4 bg-neutral-light rounded-lg"
                    >
                      <p className="text-sm text-neutral-dark mb-1">
                        {status.replace(/_/g, " ")}
                      </p>
                      <p className="text-2xl font-bold text-text">{count}</p>
                    </div>
                  )
                )}
            </div>
          </div>

          {/* Type Distribution */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-text mb-4">
              Appointment Types
            </h3>
            <div className="space-y-3">
              {analytics.typeDistribution &&
                Object.entries(analytics.typeDistribution)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const total = analytics.totalAppointments || 1;
                    const percentage = ((count / total) * 100).toFixed(1);
                    return (
                      <div key={type}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-text">
                            {type.replace(/_/g, " ")}
                          </span>
                          <span className="text-sm font-medium text-text">
                            {count} ({percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>

          {/* Peak Hours */}
          {analytics.peakHours && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-text mb-4">
                Peak Booking Hours
              </h3>
              <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                {Array.from({ length: 12 }, (_, i) => i + 9).map((hour) => {
                  const count = analytics.peakHours[hour] || 0;
                  const maxCount = Math.max(
                    ...Object.values(analytics.peakHours)
                  );
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;

                  return (
                    <div key={hour} className="flex flex-col items-center">
                      <div className="w-full h-20 flex items-end">
                        <div
                          className="w-full bg-primary rounded-t"
                          style={{ height: `${height}%` }}
                          title={`${count} appointments`}
                        ></div>
                      </div>
                      <span className="text-xs text-neutral-dark mt-1">
                        {hour}:00
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Branch Metrics */}
      {branchMetrics && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-text mb-4">
            Branch Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-neutral-dark mb-1">
                Average Visit Duration
              </p>
              <p className="text-2xl font-bold text-text">
                {branchMetrics.avgVisitDuration} mins
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-dark mb-1">
                Average Conversion Value
              </p>
              <p className="text-2xl font-bold text-text">
                ₹{branchMetrics.avgConversionValue?.toLocaleString("en-IN")}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-dark mb-1">
                Customer Satisfaction
              </p>
              <p className="text-2xl font-bold text-text">
                {branchMetrics.avgRating}/5 ⭐
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({
  title,
  value,
  trend,
  icon: Icon,
  color,
  invertTrend = false,
}) => {
  const getTrendIcon = () => {
    if (!trend || trend === 0) return null;
    const isPositive = invertTrend ? trend < 0 : trend > 0;
    return isPositive ? (
      <FiTrendingUp className="text-green-600" />
    ) : (
      <FiTrendingDown className="text-red-600" />
    );
  };

  const getTrendColor = () => {
    if (!trend || trend === 0) return "text-neutral-dark";
    const isPositive = invertTrend ? trend < 0 : trend > 0;
    return isPositive ? "text-green-600" : "text-red-600";
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon className={`text-2xl text-${color}-600`} />
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${getTrendColor()}`}>
            {getTrendIcon()}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-sm text-neutral-dark mb-1">{title}</p>
      <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
    </div>
  );
};

export default AppointmentAnalytics;
